"use client";

import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import SvgIcon from "@/components/SvgIcon";
import styles from "./booking-dialog.module.scss";

type Option = { value: number; label: string };
type Props = { label: string; options: Option[]; value: number; onChange: (value: number) => void; className?: string; testId?: string };

// Dropdown from the Figma field list (onboarding Languages 1035:206271): white sheet, 40px rows, selected row in accent with a check.
export default function BookingSelect({ label, options, value, onChange, className = "", testId }: Props) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const labelId = useId();
  const current = options.find((item) => item.value === value) || options[0];

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  const show = () => { setActive(Math.max(0, options.findIndex((item) => item.value === value))); setOpen(true); };
  const pick = (option: Option) => { setOpen(false); trigger.current?.focus(); if (option.value !== value) onChange(option.value); };
  const onKeyDown = (event: KeyboardEvent) => {
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) { event.preventDefault(); show(); }
      return;
    }
    if (event.key === "Escape") { event.preventDefault(); setOpen(false); trigger.current?.focus(); }
    else if (event.key === "Tab") setOpen(false);
    else if (event.key === "ArrowDown") { event.preventDefault(); setActive((index) => Math.min(options.length - 1, index + 1)); }
    else if (event.key === "ArrowUp") { event.preventDefault(); setActive((index) => Math.max(0, index - 1)); }
    else if (event.key === "Home") { event.preventDefault(); setActive(0); }
    else if (event.key === "End") { event.preventDefault(); setActive(options.length - 1); }
    else if (event.key === "Enter" || event.key === " ") { event.preventDefault(); if (options[active]) pick(options[active]); }
  };

  return <div ref={root} className={`${styles.selector} ${className}`} data-open={open} data-booking-select-open={open || undefined} onKeyDown={onKeyDown}>
    <span id={labelId}>{label}</span>
    <button ref={trigger} type="button" role="combobox" className={styles.selectTrigger} data-testid={testId} aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? listId : undefined} aria-activedescendant={open && options[active] ? `${listId}-${active}` : undefined} aria-labelledby={`${labelId} ${listId}-value`} onClick={() => (open ? setOpen(false) : show())}>
      <span id={`${listId}-value`}>{current?.label}</span>
    </button>
    <SvgIcon icon="chevron-down" iconType="feather" width={14} height={14} />
    {open && <ul id={listId} className={styles.selectList} role="listbox" aria-labelledby={labelId} data-testid={testId ? `${testId.replace(/-select$/, "")}-options` : undefined}>
      {options.map((option, index) => <li key={option.value} id={`${listId}-${index}`} role="option" aria-selected={option.value === value} data-active={index === active} onPointerEnter={() => setActive(index)} onClick={() => pick(option)}>
        <span>{option.label}</span>{option.value === value && <SvgIcon icon="check" iconType="feather" width={20} height={20} strokeWidth={1.5} aria-hidden="true" />}
      </li>)}
    </ul>}
  </div>;
}
